if (!customElements.get('pfd-cart-drawer')) {
  customElements.define('pfd-cart-drawer', class extends HTMLElement {
    connectedCallback() {
      this.isPage = this.tagName === 'PFD-CART-PAGE';
      this.dialog = this.querySelector('dialog');
      this.contents = this.querySelector('[data-pfd-cart-contents]');
      this.error = this.querySelector('[data-pfd-cart-error]');
      this.status = this.querySelector('[data-pfd-cart-status]');
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.addEventListener('click', event => {
        if (event.target.closest('[data-pfd-cart-close]')) this.closeMenuDrawer();
        const control = event.target.closest('[data-pfd-quantity], [data-pfd-remove]');
        if (!control || this.busy) return;
        const item = control.closest('[data-key]');
        const input = item.querySelector('[data-pfd-quantity-input]');
        let quantity = 0;
        if (!control.hasAttribute('data-pfd-remove')) {
          const step = Number(input.step) || 1;
          quantity = Number(input.value) + (control.dataset.pfdQuantity === 'plus' ? step : -step);
          if (quantity < Number(input.min)) quantity = 0;
          if (input.hasAttribute('max')) quantity = Math.min(quantity, Number(input.max));
        }
        this.changeQuantity(item.dataset.key, quantity, control.dataset.pfdQuantity || 'remove');
      }, options);
      this.addEventListener('change', event => {
        if (event.target.matches('[data-pfd-quantity-input]')) {
          const input = event.target;
          if (!input.checkValidity()) { input.reportValidity(); return; }
          this.changeQuantity(input.closest('[data-key]').dataset.key, Number(input.value), 'input');
        }
        if (event.target.matches('[data-pfd-cart-note]')) this.mutate(this.dataset.updateUrl, { note: event.target.value });
      }, options);
      this.dialog?.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeMenuDrawer();
      }, options);
      this.dialog?.addEventListener('close', () => {
        document.body.classList.remove('pfd-cart-open');
        document.querySelector('[data-pfd-cart]')?.setAttribute('aria-expanded', 'false');
        if (this.returnFocus?.isConnected) this.returnFocus.focus();
      }, options);
      this.addEventListener('submit', event => { if (this.busy) event.preventDefault(); }, options);
    }

    disconnectedCallback() {
      this.events?.abort();
      if (this.dialog?.open) this.dialog.close();
      document.body.classList.remove('pfd-cart-open');
    }

    getSectionsToRender() { return [{ id: this.isPage ? this.dataset.sectionId : 'pfd-cart-contents' }, { id: 'cart-icon-bubble' }]; }

    openCart(event, refresh = true) {
      if (this.dataset.cartType !== 'drawer') { window.location.assign(this.dataset.cartUrl); return; }
      if (!this.dialog.open) {
        this.returnFocus = event?.currentTarget || this.returnFocus || document.activeElement;
        this.dialog.showModal();
        document.body.classList.add('pfd-cart-open');
        document.querySelector('[data-pfd-cart]')?.setAttribute('aria-expanded', 'true');
      }
      if (refresh) this.refreshRender();
    }

    closeMenuDrawer() { this.dialog.close(); }

    renderContents(state, cartType = 'drawer', activeElement) {
      this.returnFocus = activeElement;
      this.sequence = (this.sequence || 0) + 1;
      const rendered = this.renderSections(state.sections);
      this.setBusy(false);
      window.cartStatus = null;
      if (!this.isPage && cartType === 'drawer') this.openCart(null, !rendered);
    }

    renderSections(sections) {
      const html = sections?.[this.getSectionsToRender()[0].id];
      if (!html) return false;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const content = parsed.querySelector('.pfd-cart-content');
      if (!content) return false;
      this.contents.replaceChildren(content);
      this.querySelector('[data-pfd-cart-count]').textContent = content.dataset.itemCount;
      const bubbleHolder = document.getElementById('cart-icon-bubble');
      if (bubbleHolder && sections['cart-icon-bubble'] != null) {
        const bubble = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('.cart-count-bubble');
        bubbleHolder.replaceChildren(...(bubble ? [bubble] : []));
      }
      this.status.textContent = `${content.dataset.itemCount} items in your cart. Subtotal ${content.dataset.total}.`;
      return true;
    }

    setBusy(busy) {
      this.busy = busy;
      this.contents.setAttribute('aria-busy', String(busy));
      if (busy) {
        this.enabledControls = [...this.contents.querySelectorAll('button:not(:disabled), input:not(:disabled), textarea:not(:disabled)')];
        this.enabledControls.forEach(control => { control.disabled = true; });
      } else {
        this.enabledControls?.forEach(control => { control.disabled = false; });
        this.enabledControls = [];
      }
    }

    showError(message = '') {
      this.error.textContent = message;
      this.error.hidden = !message;
    }

    async loadSections() {
      const url = new URL(this.dataset.sectionsUrl, window.location.origin);
      url.searchParams.set('sections', this.getSectionsToRender().map(section => section.id).join(','));
      const response = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error('Your cart could not be refreshed. Please try again.');
      return response.json();
    }

    async refreshRender() {
      if (this.busy) return;
      const sequence = this.sequence = (this.sequence || 0) + 1;
      this.showError();
      this.setBusy(true);
      try {
        const sections = await this.loadSections();
        if (sequence !== this.sequence) return;
        if (!this.renderSections(sections)) throw new Error('Your cart could not be refreshed. Please try again.');
      } catch (error) {
        if (sequence === this.sequence) this.showError(error.message);
      } finally {
        if (sequence === this.sequence) this.setBusy(false);
      }
    }

    changeQuantity(key, quantity, focusTarget) {
      if (!Number.isInteger(quantity) || quantity < 0) return;
      return this.mutate(this.dataset.changeUrl, { id: key, quantity }, { key, focusTarget });
    }

    async mutate(url, changes, focus = {}) {
      if (this.busy) return;
      this.showError();
      this.setBusy(true);
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...changes, sections: this.getSectionsToRender().map(section => section.id), sections_url: this.dataset.sectionsUrl })
        });
        const state = await response.json();
        if (!response.ok || state.errors) {
          const message = state.description || state.message || state.errors;
          throw new Error(typeof message === 'string' ? message : 'This quantity is not available. Please try a different amount.');
        }
        if (!this.renderSections(state.sections) && !this.renderSections(await this.loadSections())) {
          throw new Error('Your cart was updated, but the display could not refresh. Reopen the cart to see the latest total.');
        }
        if ((this.isPage || this.dialog?.open) && focus.key) {
          const item = [...this.querySelectorAll('[data-key]')].find(row => row.dataset.key === focus.key);
          const control = focus.focusTarget === 'input' ? item?.querySelector('[data-pfd-quantity-input]') : item?.querySelector(`[data-pfd-quantity="${focus.focusTarget}"]`);
          (control && !control.disabled ? control : this.querySelector('[data-pfd-cart-close], [data-pfd-cart-count]'))?.focus();
        }
      } catch (error) {
        this.showError(error.message || 'Your cart could not be updated. Please try again.');
        try { this.renderSections(await this.loadSections()); } catch { /* Keep the existing cart and visible error if offline. */ }
      } finally { this.setBusy(false); }
    }
  });
}
if (!customElements.get('pfd-cart-page')) {
  customElements.define('pfd-cart-page', class extends customElements.get('pfd-cart-drawer') {});
}
